import React from 'react';
import { motion } from 'framer-motion';

/**
 * Reusable section wrapper for editorial "chapter" style sections.
 * Supports both dark (`section-dark`) and light (`section-light`) themes.
 */
export const SectionShell = ({
  id,
  testId,
  theme = 'dark', // 'dark' | 'light'
  index,
  eyebrow,
  title,
  intro,
  children,
  align = 'grid', // 'grid' | 'stack'
}) => {
  const isDark = theme === 'dark';
  return (
    <section
      id={id}
      data-testid={testId}
      className={`section ${isDark ? 'section-dark' : 'section-light'}`}
    >
      <div className="container-narrow">
        {/* Header */}
        <div
          className={`grid grid-cols-12 gap-6 md:gap-10 ${
            align === 'stack' ? '' : 'mb-14 md:mb-20'
          }`}
        >
          <div className="col-span-12 md:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-3"
            >
              {index && (
                <span
                  className={`index-chip ${
                    isDark
                      ? 'text-[color:var(--gold-2)]'
                      : 'text-[color:var(--gold-3)]'
                  }`}
                >
                  {index}
                </span>
              )}
              {eyebrow && (
                <span
                  className={`eyebrow ${isDark ? '' : 'eyebrow-light'}`}
                >
                  {eyebrow}
                </span>
              )}
            </motion.div>
          </div>
          <div className="col-span-12 md:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0px' }}
              transition={{ duration: 0.75, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              className={`font-serif text-[34px] sm:text-[44px] md:text-[54px] leading-[1.05] tracking-[-0.015em] ${
                isDark ? 'text-[color:var(--stone)]' : 'text-[color:var(--charcoal)]'
              }`}
            >
              {title}
            </motion.h2>
            {intro && (
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-10% 0px' }}
                transition={{ duration: 0.75, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                className={`mt-6 text-[16px] md:text-[17px] leading-[1.6] max-w-[720px] ${
                  isDark
                    ? 'text-[color:var(--stone-2)]'
                    : 'text-[color:var(--charcoal-2)]'
                }`}
              >
                {intro}
              </motion.p>
            )}
          </div>
        </div>

        {/* Divider */}
        {align !== 'stack' && (
          <div
            className={`h-px w-full mb-12 md:mb-16 ${
              isDark ? 'bg-[color:var(--rule-dark)]' : 'bg-[color:var(--rule-light)]'
            }`}
          />
        )}

        {children}
      </div>
    </section>
  );
};

export default SectionShell;
